import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import { useAuth } from '@/core/auth/AuthContext';
import { supabase } from '@/core/database/supabase';
import { roleLabels, navigationConfig } from '@/config/navigation.config';
import { useNotifications } from '@/modules/dashboard/hooks/useNotifications';
import { TopbarSearch } from '@/components/ui';
import { cn } from '@/utils/cn';

interface TopbarProps {
  sidebarCollapsed: boolean;
  onMobileMenuToggle: () => void;
}

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface WeatherState {
  temperature: string;
  condition: string;
}

const fallbackWeather: WeatherState = {
  temperature: '28°C',
  condition: 'Nhiều mây',
};

function getBreadcrumb(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [{ label: 'Trang chủ', path: '/' }];

  if (segments.length === 0) return breadcrumbs;

  const allItems = navigationConfig.flatMap((group) => group.items);
  
  // Mapping for common path segments to human-readable Vietnamese labels
  const segmentLabels: Record<string, string> = {
    'new': 'Thêm mới',
    'edit': 'Chỉnh sửa',
    'detail': 'Chi tiết',
    'profile': 'Trang cá nhân',
    'settings': 'Cài đặt',
    'notifications': 'Thông báo',
    'reports': 'Báo cáo',
    'approvals': 'Phê duyệt',
    'inventory': 'Kho vật tư',
    'users': 'Nhân sự',
    'roles': 'Quyền & Vai trò',
    'projects': 'Dự án',
    'work-orders': 'Công việc',
    'requests': 'Đề xuất vật tư',
    'equipment': 'Thiết bị',
    'construction': 'Thi công',
    'maintenance': 'Bảo dưỡng',
  };

  let currentPath = '';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // 1. Try to find in navigationConfig
    const matched = allItems.find(item => item.path === currentPath);
    if (matched) {
      breadcrumbs.push({ label: matched.label, path: matched.path });
      return;
    }

    // 2. Try common labels mapping
    if (segmentLabels[segment]) {
      breadcrumbs.push({ label: segmentLabels[segment], path: currentPath });
      return;
    }

    // 3. Fallback for IDs/UUIDs or unknown segments
    const isId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) || /^\d+$/.test(segment);
    if (isId) {
      // Special case for projects detail
      if (segments[index - 1] === 'projects') {
        breadcrumbs.push({ label: 'Chi tiết dự án', path: currentPath });
      } else {
        breadcrumbs.push({ label: 'Chi tiết', path: currentPath });
      }
    } else {
      // Capitalize first letter as last resort
      const fallbackLabel = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      breadcrumbs.push({ label: fallbackLabel, path: currentPath });
    }
  });

  // Handle special case for /projects/:id (ensure "Dự án" is present if missed)
  if (segments.length >= 2 && segments[0] === 'projects' && breadcrumbs.length === 2) {
     // This means projects was handled but detail was added after. 
     // We want to make sure it looks like Home > Projects > Detail
  }

  return breadcrumbs;
}

export function Topbar({ sidebarCollapsed, onMobileMenuToggle }: TopbarProps) {
  const { user } = useCurrentUser();
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [weather, setWeather] = useState<WeatherState>(fallbackWeather);
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications();

  const breadcrumbs = useMemo(() => getBreadcrumb(location.pathname), [location.pathname]);
  const safeName = profile?.full_name || user.fullName || 'Người dùng';
  const safeRoleCode = profile?.role_code || user.roleCode || 'staff';
  const safeRole = profile?.job_title || roleLabels[safeRoleCode as keyof typeof roleLabels] || 'Nhân sự';
  const avatarUrl = profile?.avatar_path || user.avatarPath || '';
  const initials = getInitials(safeName);

  useEffect(() => {
    let isMounted = true;

    async function loadWeather() {
      if (!profile?.company_id) {
        setWeather(fallbackWeather);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('team_daily_reports')
          .select('weather_note')
          .eq('company_id', profile.company_id)
          .not('weather_note', 'is', null)
          .order('report_date', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        if (!isMounted) return;

        const condition = typeof data?.weather_note === 'string' && data.weather_note.trim()
          ? data.weather_note.trim()
          : fallbackWeather.condition;
        setWeather({ temperature: fallbackWeather.temperature, condition });
      } catch (error) {
        console.warn('[Topbar] weather fallback:', error);
        if (isMounted) setWeather(fallbackWeather);
      }
    }

    loadWeather();
    return () => {
      isMounted = false;
    };
  }, [profile?.company_id]);

  async function handleSignOut(event?: React.MouseEvent) {
    event?.preventDefault();
    setShowUserMenu(false);
    try {
      await signOut();
    } catch (error) {
      console.error('[Topbar] signOut error:', error);
      const base = (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '/').replace(/\/$/, '');
      window.location.href = `${base}/auth/login`;
    }
  }

  function closeAll() {
    setShowUserMenu(false);
    setShowNotifications(false);
  }

  return (
    <header
      className={cn(
        "sticky top-0 z-[90] h-16 w-full bg-white border-b border-slate-100",
        "flex items-center justify-between gap-3 px-3 sm:px-4 lg:px-6 transition-all duration-300"
      )}
    >
      {/* Left: Mobile Toggle & Breadcrumbs */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onMobileMenuToggle}
          className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-50 active:scale-95 transition-all"
        >
          <i className="ri-menu-2-line text-lg" />
        </button>

        <BreadcrumbBlock breadcrumbs={breadcrumbs} />
      </div>

      {/* Right: Search, Weather, Actions, User */}
      <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
        <div className="hidden xl:block xl:w-64">
          <TopbarSearch
            value={searchValue}
            onChange={setSearchValue}
            onSubmit={(value) => {
              const query = value.trim();
              if (query) navigate(`/projects?search=${encodeURIComponent(query)}`);
            }}
            placeholder="Tìm kiếm..."
            className="w-full"
          />
        </div>

        <WeatherBlock weather={weather} />

        <div className="h-6 w-[1px] bg-slate-100 hidden xl:block" />

        <div className="flex items-center gap-1">
          {/* Notifications */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowNotifications((current) => !current);
                setShowUserMenu(false);
              }}
              className={cn(
                "w-9 h-9 flex items-center justify-center rounded-md transition-all active:scale-95",
                showNotifications ? "bg-emerald-50 text-emerald-600" : "text-slate-600 hover:bg-slate-50"
              )}
            >
              <i className="ri-notification-3-line text-lg" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] border-2 border-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <NotificationPanel
                notifications={notifications || []}
                unreadCount={unreadCount || 0}
                markRead={markRead}
                markAllRead={markAllRead}
                onClose={closeAll}
              />
            )}
          </div>

          {/* Help/Settings */}
          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="hidden sm:flex w-9 h-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-50 active:scale-95 transition-all"
          >
            <i className="ri-question-line text-lg" />
          </button>
        </div>

        <div className="h-6 w-[1px] bg-slate-100 hidden sm:block" />

        {/* User Profile */}
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setShowUserMenu((current) => !current);
              setShowNotifications(false);
            }}
            className={cn(
              "flex items-center gap-2 p-1 pr-2 rounded-md transition-all active:scale-95",
              showUserMenu ? "bg-slate-50" : "hover:bg-slate-50"
            )}
          >
            <AvatarBlock avatarUrl={avatarUrl} name={safeName} initials={initials} />
            <div className="hidden lg:flex flex-col text-left min-w-0 leading-tight">
              <span className="sm:text-sm text-xs text-slate-800 truncate max-w-[120px]">{safeName}</span>
              <span className="text-xs text-slate-400 truncate max-w-[120px]">{safeRole}</span>
            </div>
            <i className={cn(
              "ri-arrow-down-s-line text-slate-400 transition-transform hidden sm:block",
              showUserMenu && "rotate-180"
            )} />
          </button>

          {showUserMenu && (
            <UserPanel
              name={safeName}
              role={safeRole}
              avatarUrl={avatarUrl}
              initials={initials}
              onClose={closeAll}
              onSignOut={handleSignOut}
            />
          )}
        </div>
      </div>

      {/* Overlay for closing dropdowns */}
      {(showUserMenu || showNotifications) && (
        <div
          className="fixed inset-0 z-[90] cursor-default"
          onClick={closeAll}
        />
      )}
    </header>
  );
}

function BreadcrumbBlock({ breadcrumbs }: { breadcrumbs: BreadcrumbItem[] }) {
  const lastCrumb = breadcrumbs[breadcrumbs.length - 1];

  return (
    <nav className="flex items-center gap-2 min-w-0">
      {/* Mobile Page Title */}
      <div className="sm:hidden flex items-center min-w-0">
        <span className="text-sm font-medium text-slate-800 truncate max-w-[180px]">
          {lastCrumb?.label}
        </span>
      </div>

      {/* Desktop Breadcrumbs */}
      <div className="hidden sm:flex items-center gap-2 min-w-0">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <div key={`${crumb.path}-${index}`} className="flex items-center gap-2 min-w-0">
              {index > 0 && <ChevronRight className="w-3 h-3 text-slate-300 flex-shrink-0" />}
              {isLast ? (
                <span className="sm:text-sm text-xs text-slate-600 truncate max-w-[200px]">{crumb.label}</span>
              ) : (
                <Link 
                  to={crumb.path} 
                  className="sm:text-sm text-xs text-slate-400 hover:text-emerald-600 transition-colors truncate max-w-[200px]"
                >
                  {crumb.label}
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}

function WeatherBlock({ weather }: { weather: WeatherState }) {
  return (
    <div className="hidden xl:flex items-center gap-3">
      <div className="relative w-8 h-8 flex items-center justify-center rounded-md bg-slate-50">
        <i className="ri-sun-fill text-amber-400 text-lg absolute -top-1 -right-1" />
        <i className="ri-cloudy-2-fill text-sky-300 text-xl absolute bottom-0 left-0" />
      </div>
      <div className="flex flex-col leading-tight">
        <span className="sm:text-sm text-xs text-slate-800">{weather.temperature}</span>
        <span className="text-xs text-slate-400">{weather.condition}</span>
      </div>
    </div>
  );
}

function NotificationPanel({ notifications, unreadCount, markRead, markAllRead, onClose }: NotificationPanelProps) {
  const navigate = useNavigate();

  return (
    <div className="absolute right-0 top-[calc(100%+8px)] w-80 bg-white border border-slate-100 rounded-md shadow-sm z-[110] overflow-hidden">
      <div className="p-3 border-b border-slate-50 flex items-center justify-between">
        <h3 className="sm:text-sm text-xs text-slate-900">Thông báo</h3>
        {unreadCount > 0 && (
          <button 
            onClick={markAllRead} 
            className="text-xs text-emerald-600 hover:text-emerald-700"
          >
            Đọc tất cả
          </button>
        )}
      </div>

      <div className="max-h-80 overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-slate-200">
        {notifications.length === 0 ? (
          <div className="p-8 text-center space-y-2">
            <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto">
              <i className="ri-notification-off-line text-slate-300 text-xl" />
            </div>
            <p className="sm:text-sm text-xs text-slate-400">Chưa có thông báo nào</p>
          </div>
        ) : (
          notifications.slice(0, 8).map((item) => {
            const isUnread = item.status_code === 'unread';
            return (
              <button
                key={item.id}
                onClick={() => {
                  markRead(item.id);
                  if (item.link) navigate(item.link);
                  onClose();
                }}
                className={cn(
                  "w-full text-left rounded-md p-3 space-y-1 transition-all",
                  isUnread ? "bg-emerald-50/50 hover:bg-emerald-50" : "hover:bg-slate-50"
                )}
              >
                <p className={cn("sm:text-sm text-xs line-clamp-2", isUnread ? "text-slate-900" : "text-slate-600")}>
                  {item.title}
                </p>
                <p className="text-xs text-slate-400">{formatTime(item.created_at)}</p>
              </button>
            );
          })
        )}
      </div>

      <Link 
        to="/notifications" 
        onClick={onClose} 
        className="block p-3 text-center sm:text-sm text-xs text-emerald-600 border-t border-slate-50 hover:bg-slate-50 transition-all"
      >
        Xem tất cả
      </Link>
    </div>
  );
}

function UserPanel({ name, role, avatarUrl, initials, onClose, onSignOut }: UserPanelProps) {
  const menuItems = [
    { label: 'Hồ sơ cá nhân', icon: 'ri-user-line', path: '/profile' },
    { label: 'Cài đặt hệ thống', icon: 'ri-settings-3-line', path: '/settings' },
    { label: 'Trợ giúp & Phản hồi', icon: 'ri-customer-service-2-line', path: '/help' },
  ];

  return (
    <div className="absolute right-0 top-[calc(100%+8px)] w-64 bg-white border border-slate-100 rounded-md shadow-sm z-[110] overflow-hidden">
      <div className="p-4 bg-slate-50/50 flex items-center gap-3">
        <AvatarBlock avatarUrl={avatarUrl} name={name} initials={initials} />
        <div className="min-w-0">
          <p className="sm:text-sm text-xs text-slate-900 truncate">{name}</p>
          <p className="text-xs text-slate-400 truncate">{role}</p>
        </div>
      </div>

      <div className="p-2 space-y-1">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={onClose}
            className="flex items-center gap-3 px-3 py-2 rounded-md sm:text-sm text-xs text-slate-600 hover:bg-slate-50 hover:text-emerald-600 transition-all"
          >
            <i className={cn(item.icon, "text-lg")} />
            {item.label}
          </Link>
        ))}
      </div>

      <div className="p-2 border-t border-slate-50">
        <button
          onClick={onSignOut}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md sm:text-sm text-xs text-rose-600 hover:bg-rose-50 transition-all"
        >
          <i className="ri-logout-box-r-line text-lg" />
          Đăng xuất tài khoản
        </button>
      </div>
    </div>
  );
}

function AvatarBlock({ avatarUrl, name, initials }: { avatarUrl: string; name: string; initials: string }) {
  const [hasError, setHasError] = useState(false);

  return (
    <div className="w-9 h-9 rounded-full overflow-hidden bg-orange-100 border border-slate-200 shadow-sm flex-shrink-0 flex items-center justify-center">
      {avatarUrl && !hasError ? (
        <img 
          src={avatarUrl} 
          alt={name} 
          onError={() => setHasError(true)}
          className="w-full h-full object-cover" 
        />
      ) : (
        <span className="sm:text-sm text-xs text-orange-700 tracking-tighter">
          {initials}
        </span>
      )}
    </div>
  );
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'U';
  return words.slice(-2).map((word) => word.charAt(0)).join('');
}

function formatTime(value?: string): string {
  if (!value) return 'Vừa xong';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Vừa xong';
  return date.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface NotificationPanelProps {
  notifications: Array<{ id: string; title?: string; status_code?: string; link?: string; created_at?: string }>;
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
  onClose: () => void;
}

interface UserPanelProps {
  name: string;
  role: string;
  avatarUrl: string;
  initials: string;
  onClose: () => void;
  onSignOut: (event: React.MouseEvent) => void;
}
